import { useState, useEffect, useRef, useCallback } from "react";
import { GameProps } from "shared/games/types/GameConfiguration";
import { GameQuestionCanYouPan } from "shared/games/types/GameQuestions";
import { GameAnswerCanYouPan } from "shared/games/types/GameAnswers";
import { useMountEffect, useUnmountEffect } from "@react-hookz/web";
import { storagePath } from "apiRoutes/storagePath";
import { SingleAudioPlayer } from "shared/AudioPlayers/SingleAudioPlayer";
import useAutoToggle from "ui/templates/Game/hooks/useAutoToggle";
import * as GameLayout from "ui/templates/Game/LayoutComponents";
import MobileFloatingAutoToggleButton from "ui/templates/Game/molecules/MobileFloatingAutoToggleButton";
import Button from "ui/atoms/Button/Button";
import Switch, { SwitchItem } from "ui/atoms/Switch/Switch";
import CanYouPanGameTemplate from "../templates/CanYouPanGameTemplate";
import { audioCtx } from "util/audioContext";
import { referenceTrackSteps, refrenceTrackIds } from "./referenceTrackConfig";
import ReferenceTrack from "ui/organisms/ReferenceTrack/ReferenceTrack";

type Props = GameProps<GameQuestionCanYouPan, GameAnswerCanYouPan>;
export type PlaybackState = "mix" | "reference";

export default function CanYouPanGame({
  question,
  onLoaded,
  onSubmit,
  controlledPlayback,
  mobileToolbarStyle,
  submitInProgress,
  referenceTrackDisabled,
}: Props) {
  const [mixPan, setMixPan] = useState<number>(0);
  const audioPlayer = useRef<SingleAudioPlayer>();
  const {
    playbackState,
    setPlaybackState,
    autoToggle,
    setAutoToggle,
    secondsTillChange,
  } = useAutoToggle<PlaybackState>({
    defaultPlaybackState: "mix",
    looop: question.looop,
    getNextPlaybackState: (prev) => (prev === "mix" ? "reference" : "mix"),
    measures: 4,
    enabled: controlledPlayback !== "pause",
  });

  useMountEffect(() => {
    const player = new SingleAudioPlayer(
      storagePath(
        `${question.looop.base_audio_url}${question.instrument.audio_files.default}`
      ),
      question.looop
    );

    player.load().then(() => {
      if (controlledPlayback !== "pause") {
        player.start();
      }

      onLoaded();
    });

    audioPlayer.current = player;
  });

  useEffect(() => {
    if (controlledPlayback === "pause") {
      audioPlayer.current?.stop();
    } else if (controlledPlayback === "play") {
      audioPlayer.current?.start();
    }
  }, [controlledPlayback]);

  useUnmountEffect(() => {
    audioPlayer.current?.dispose();
    audioPlayer.current = undefined;
  });

  useEffect(() => {
    const pan = playbackState === "mix" ? mixPan : question.panDegree;

    audioPlayer.current?.setPlaybackStatus(
      {
        pan: convertPanDegreesToPan(pan),
      },
      audioCtx().currentTime + 0.2
    );
  }, [mixPan, playbackState, question.panDegree]);

  const handleSubmit = useCallback(() => {
    const correct = mixPan === question.panDegree;

    onSubmit({
      type: question.type,
      correct,
      submission: mixPan,
    });
  }, [mixPan, onSubmit, question.panDegree, question.type]);

  return (
    <>
      <GameLayout.Toolbar mobileStyle={mobileToolbarStyle}>
        <div className={"flex justify-between"}>
          <div className={"flex items-center"}>
            <div className={"mr-2"} id={refrenceTrackIds.playbackState}>
              <Switch
                size="large"
                items={items}
                value={playbackState}
                onChange={(value) => {
                  setAutoToggle(false);
                  setPlaybackState(value);
                }}
                wholeButtonToggle
              />
            </div>

            <div className={"hidden md:block"}>
              <Button
                theme={"default"}
                size="large"
                outline
                active={autoToggle}
                onClick={() => setAutoToggle((x) => !x)}
              >
                Auto Toggle {secondsTillChange}
              </Button>
            </div>
          </div>

          <Button
            id={refrenceTrackIds.submit}
            theme="green"
            size="large"
            onClick={handleSubmit}
            loading={submitInProgress}
          >
            Submit
          </Button>
        </div>
      </GameLayout.Toolbar>

      <MobileFloatingAutoToggleButton
        mobileToolbarStyle={mobileToolbarStyle}
        autoToggle={autoToggle}
        setAutoToggle={setAutoToggle}
        secondsTillChange={secondsTillChange}
      />

      <GameLayout.Container>
        <CanYouPanGameTemplate
          pan={mixPan}
          setPan={setMixPan}
          playbackState={playbackState}
          question={question}
        />
      </GameLayout.Container>

      <ReferenceTrack
        localStorageKey={"can-you-pan"}
        getSteps={referenceTrackSteps}
        disabled={referenceTrackDisabled}
      />
    </>
  );
}

const items: SwitchItem<PlaybackState>[] = [
  { label: { mobile: "Mix", desktop: "Mix" }, value: "mix" },
  {
    label: { mobile: "Ref", desktop: "Reference" },
    value: "reference",
  },
];

// Far left -90 degrees pan === -1 webaudio pan, far right 90 degrees pan === 1 webaudio pan
function convertPanDegreesToPan(panDegrees: number): number {
  const pan = panDegrees / 90;

  // it breaks when you do -1 or 1 straight -- so just use -0.999 and 0.999
  if (pan <= -1) {
    return -0.999;
  }

  if (pan >= 1) {
    return 0.999;
  }

  return panDegrees / 90;
}
